import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { STORAGE } from '@constants/storage';
import { store } from '@lib/storage';
import { alertToneReady, armAlertTone, onAlertToneState, playAlertTone, unlockAlertTone } from '@lib/utils/alertTone';

const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const read = () => store.get<boolean>(STORAGE.alertSound, false);

/** Whether the critical-alert tone is on (per device). Readable outside React, e.g. in a stream handler. */
export const alertSoundOn = read;

/**
 * The critical-alert tone switch, shared by every place that shows it (account menu, monitor mode): whether it is
 * on, whether the browser still waits for an interaction before it may play (`locked`: say so next to the switch,
 * a screen nobody touches would otherwise stay silent), and the setter. Switching it on is a click, so audio
 * unlocks right then and a preview plays.
 */
export function useAlertSound(): { on: boolean; locked: boolean; set: (on: boolean) => void } {
  const on = useSyncExternalStore(subscribe, read, () => false);
  const ready = useSyncExternalStore(onAlertToneState, alertToneReady, () => false);
  useEffect(() => {
    if (on) armAlertTone();
  }, [on]);
  const set = useCallback((next: boolean) => {
    store.set(STORAGE.alertSound, next);
    if (next) void unlockAlertTone().then((ok) => ok && playAlertTone(true));
    listeners.forEach((l) => l());
  }, []);
  return { on, locked: on && !ready, set };
}
